import { Grid } from "antd";

const { useBreakpoint } = Grid;

export interface ResponsiveState {
  /** True if screen is xs or sm (< 768px) */
  isMobile: boolean;
  /** True if screen is md (768px - 991px) */
  isTablet: boolean;
  /** True if screen is lg or above (>= 992px) */
  isDesktop: boolean;
  /** Current breakpoint name */
  breakpoint: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  /** Raw Ant Design breakpoint object */
  screens: ReturnType<typeof useBreakpoint>;
}

/**
 * Hook to detect current responsive breakpoint.
 * Uses Ant Design's Grid.useBreakpoint() internally.
 *
 * Breakpoints:
 * - xs: 0px (mobile phones)
 * - sm: 576px (large phones)
 * - md: 768px (tablets)
 * - lg: 992px (small desktops)
 * - xl: 1200px (desktops)
 * - xxl: 1600px (large desktops)
 */
export const useResponsive = (): ResponsiveState => {
  const screens = useBreakpoint();

  // Mobile: xs and sm (< 768px)
  const isMobile = !screens.md;

  // Tablet: md only (768px - 991px)
  const isTablet = !!screens.md && !screens.lg;

  // Desktop: lg and above (>= 992px)
  const isDesktop = !!screens.lg;

  // Determine current breakpoint (highest active)
  let breakpoint: ResponsiveState["breakpoint"] = "xs";
  if (screens.xxl) breakpoint = "xxl";
  else if (screens.xl) breakpoint = "xl";
  else if (screens.lg) breakpoint = "lg";
  else if (screens.md) breakpoint = "md";
  else if (screens.sm) breakpoint = "sm";

  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
    screens,
  };
};
